import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import ConfirmationGuard from './components/ConfirmationGuard';
import GuestRoute from './components/GuestRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

// Public
import Book from './pages/public/Book';
import GiggerProfile from './pages/public/GiggerProfile';
import Home from './pages/public/Home';
import HowItWorks from './pages/public/HowItWorks';
import NotFound from './pages/public/NotFound';
import Services from './pages/public/Services';
import Track from './pages/public/Track';
import WhatIsGiggs from './pages/public/WhatIsGiggs';

// Auth
import AdminLogin from './pages/auth/AdminLogin';
import AdminRegister from './pages/auth/AdminRegister';
import ClientRegister from './pages/auth/ClientRegister';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import ProviderRegister from './pages/auth/ProviderRegister';
import ResetPassword from './pages/auth/ResetPassword';

// Client
import ClientBookings from './pages/client/Bookings';
import ClientDashboard from './pages/client/Dashboard';
import ClientInvoices from './pages/client/Invoices';
import ClientTrackBooking from './pages/client/TrackBooking';

// Admin
import AdminBookings from './pages/admin/Bookings';
import AdminDashboard from './pages/admin/Dashboard';
import AdminFinancialLedger from './pages/admin/AdminFinancialLedger';
import AdminInquiries from './pages/admin/inquiries';
import AdminMessages from './pages/admin/messages';
import AdminServices from './pages/admin/Services';
import AdminUsers from './pages/admin/UserManagement';
import AdminVerifications from './pages/admin/Verifications';

// Provider
import ProviderBookings from './pages/provider/Bookings';
import ProviderDashboard from './pages/provider/Dashboard';
import ProviderMessages from './pages/provider/Messages';
import ProviderSettings from './pages/provider/Settings';

const guestOnly = (element) => <GuestRoute>{element}</GuestRoute>;

const App = () => {
  return (
    <BrowserRouter>
      <ConfirmationGuard>
        <AuthProvider>
          <ScrollToTop />
          <ChatProvider>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/what-is-giggs" element={<WhatIsGiggs />} />
              <Route path="/book" element={<Book />} />
              <Route path="/track" element={<Track />} />
              <Route path="/track/:trackingId" element={<Track />} />
              <Route path="/gigger/:id" element={<GiggerProfile />} />

              {/* Auth — signed-in users are sent back to their own portal */}
              <Route path="/login" element={guestOnly(<Login />)} />
              <Route path="/client-register" element={guestOnly(<ClientRegister />)} />
              <Route path="/provider-register" element={guestOnly(<ProviderRegister />)} />
              <Route path="/forgot-password" element={guestOnly(<ForgotPassword />)} />
              <Route path="/reset-password" element={guestOnly(<ResetPassword />)} />
              <Route path="/admin-login" element={guestOnly(<AdminLogin />)} />
              <Route path="/admin-register" element={guestOnly(<AdminRegister />)} />

              {/* Friendly aliases for older/public registration links */}
              <Route path="/register" element={<Navigate to="/client-register" replace />} />
              <Route path="/join" element={<Navigate to="/client-register" replace />} />
              <Route path="/become-a-gigger" element={<Navigate to="/provider-register" replace />} />

              {/* Admin */}
              <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/bookings" element={<ProtectedRoute role="admin"><AdminBookings /></ProtectedRoute>} />
              <Route path="/admin/financials" element={<ProtectedRoute role="admin"><AdminFinancialLedger /></ProtectedRoute>} />
              <Route path="/admin/payments" element={<Navigate to="/admin/financials?tab=payouts" replace />} />
              <Route path="/admin/invoices" element={<Navigate to="/admin/financials" replace />} />
              <Route path="/admin/services" element={<ProtectedRoute role="admin"><AdminServices /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/verifications" element={<ProtectedRoute role="admin"><AdminVerifications /></ProtectedRoute>} />
              <Route path="/admin/messages" element={<ProtectedRoute role="admin"><AdminMessages /></ProtectedRoute>} />
              <Route path="/admin/inquiries" element={<ProtectedRoute role="admin"><AdminInquiries /></ProtectedRoute>} />

              {/* Provider */}
              <Route path="/provider/dashboard" element={<ProtectedRoute role="provider"><ProviderDashboard /></ProtectedRoute>} />
              <Route path="/provider/messages" element={<ProtectedRoute role="provider"><ProviderMessages /></ProtectedRoute>} />
              <Route path="/provider/settings" element={<ProtectedRoute role="provider"><ProviderSettings /></ProtectedRoute>} />
              <Route path="/provider/bookings" element={<ProtectedRoute role="provider"><ProviderBookings /></ProtectedRoute>} />

              {/* Client */}
              <Route path="/client/dashboard" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
              <Route path="/client/bookings" element={<ProtectedRoute role="client"><ClientBookings /></ProtectedRoute>} />
              <Route path="/client/invoices" element={<ProtectedRoute role="client"><ClientInvoices /></ProtectedRoute>} />
              <Route path="/client/track" element={<ProtectedRoute role="client"><ClientTrackBooking /></ProtectedRoute>} />
              <Route path="/client/track/:trackingId" element={<ProtectedRoute role="client"><ClientTrackBooking /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </ChatProvider>
        </AuthProvider>
      </ConfirmationGuard>
    </BrowserRouter>
  );
};

export default App;
